import { useEffect, useRef, useCallback } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { DEPOT_LOCATION, FINLAND_CENTER } from "@/config/locations";

// Fix Leaflet default marker icon
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

export interface InstallationMarker {
  id: string;
  orderId: string;
  orderNumber: string;
  customerName: string;
  cemeteryName: string;
  cemeteryCity: string;
  taskType: string;
  orderType: string;
  orderStatus: string;
  lat: number;
  lng: number;
}

export interface OptimizedWaypoint {
  position: number;
  waypointId: string;
  name: string;
  lat: number;
  lng: number;
}

export interface OptimizeResult {
  success: boolean;
  totalDistanceKm: number;
  estimatedDurationMin: number;
  routeGeometry: GeoJSON.GeoJsonObject | null;
  optimizedOrder: OptimizedWaypoint[];
  depot?: { lat: number; lng: number; name: string };
}

interface UseLoadCreationMapParams {
  containerRef: React.RefObject<HTMLDivElement>;
  markers: InstallationMarker[];
  selectedIds: Set<string>;
  onMarkerClick: (id: string) => void;
  optimizeResult: OptimizeResult | null;
}

interface UseLoadCreationMapReturn {
  fitBounds: () => void;
}

export function useLoadCreationMap({
  containerRef,
  markers,
  selectedIds,
  onMarkerClick,
  optimizeResult,
}: UseLoadCreationMapParams): UseLoadCreationMapReturn {
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<L.Marker[]>([]);
  const routeLayerRef = useRef<L.GeoJSON | null>(null);
  const depotMarkerRef = useRef<L.Marker | null>(null);
  const initialFitDoneRef = useRef(false);

  // 1. Map initialization with depot marker
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    mapRef.current = L.map(containerRef.current).setView(FINLAND_CENTER, 6);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(mapRef.current);

    // Add depot marker (always visible)
    const depotIcon = L.divIcon({
      className: "custom-marker",
      html: `<div style="
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background-color: #7c3aed;
        border: 4px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.4);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 18px;
      ">🏠</div>`,
      iconSize: [36, 36],
      iconAnchor: [18, 18],
    });

    depotMarkerRef.current = L.marker([DEPOT_LOCATION.lat, DEPOT_LOCATION.lng], { icon: depotIcon })
      .addTo(mapRef.current)
      .bindPopup(`
        <div style="min-width: 150px;">
          <strong>🏠 Verstas</strong><br/>
          Kuntolantie 52<br/>
          <span style="color: #666;">61300 Kurikka</span><br/>
          <span style="font-size: 11px; color: #7c3aed;">Lähtö- ja paluupiste</span>
        </div>
      `);

    return () => {
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, [containerRef]);

  // 2. Update markers on map
  useEffect(() => {
    if (!mapRef.current) return;

    // Clear existing markers
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];

    if (markers.length === 0) return;

    // Helper to get task type label
    const getTaskTypeLabel = (taskType: string, orderType: string) => {
      if (taskType === "pickup") return "Nouto";
      if (orderType === "pickup_work") return "Palautus";
      return "Asennus";
    };

    // Helper to get color based on task type
    const getTaskTypeColor = (taskType: string, orderType: string) => {
      if (taskType === "pickup") return "#f97316"; // orange
      if (orderType === "pickup_work") return "#14b8a6"; // teal
      return "#22c55e"; // green for new installation
    };

    markers.forEach(marker => {
      const isSelected = selectedIds.has(marker.id);
      const isReady = marker.orderStatus === "ready_for_installation";
      const taskTypeLabel = getTaskTypeLabel(marker.taskType, marker.orderType);
      
      // Color based on selection, status, and task type
      const getMarkerColor = () => {
        if (isSelected) return getTaskTypeColor(marker.taskType, marker.orderType);
        if (!isReady) return "#f59e0b"; // amber for not ready
        // Unselected but ready - show task type color but muted
        if (marker.taskType === "pickup") return "#fdba74"; // orange-300
        if (marker.orderType === "pickup_work") return "#5eead4"; // teal-300
        return "#9ca3af"; // gray for new installation
      };

      // Get icon symbol based on task type
      const getMarkerSymbol = () => {
        if (isSelected) return "✓";
        if (marker.taskType === "pickup") return "↑"; // pickup to workshop
        if (marker.orderType === "pickup_work") return "↻"; // return from workshop
        return "";
      };
      
      // Create custom icon based on selection state and status
      const customIcon = L.divIcon({
        className: "custom-marker",
        html: `<div style="
          width: 30px;
          height: 30px;
          border-radius: 50%;
          background-color: ${getMarkerColor()};
          border: 3px solid white;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 12px;
        ">${getMarkerSymbol()}</div>`,
        iconSize: [30, 30],
        iconAnchor: [15, 15],
      });

      const statusLabel = marker.orderStatus === "ready_for_installation" ? "Valmis asennukseen" : 
                         marker.orderStatus === "confirmed" ? "Vahvistettu" : marker.orderStatus;

      const leafletMarker = L.marker([marker.lat, marker.lng], { icon: customIcon })
        .addTo(mapRef.current!)
        .bindPopup(`
          <div style="min-width: 150px;">
            <strong>${marker.orderNumber}</strong><br/>
            ${marker.customerName}<br/>
            <span style="color: #666;">${marker.cemeteryName}, ${marker.cemeteryCity}</span><br/>
            <span style="font-size: 11px; color: ${getTaskTypeColor(marker.taskType, marker.orderType)}; font-weight: 500;">${taskTypeLabel}</span><br/>
            <span style="font-size: 11px; color: ${!isReady ? '#f59e0b' : '#888'};">${statusLabel}</span>
          </div>
        `)
        .on("click", () => {
          onMarkerClick(marker.id);
        });

      markersRef.current.push(leafletMarker);
    });

    // Fit bounds to markers only on initial load
    if (markers.length > 0 && !initialFitDoneRef.current) {
      const bounds = L.latLngBounds(markers.map(m => [m.lat, m.lng]));
      mapRef.current.fitBounds(bounds, { padding: [50, 50] });
      initialFitDoneRef.current = true;
    }
  }, [markers, selectedIds, onMarkerClick]);

  // 3. Draw route on map when optimized
  useEffect(() => {
    if (!mapRef.current) return;

    // Clear existing route
    if (routeLayerRef.current) {
      routeLayerRef.current.remove();
      routeLayerRef.current = null;
    }

    if (!optimizeResult?.routeGeometry) return;

    // Add route line
    routeLayerRef.current = L.geoJSON(optimizeResult.routeGeometry, {
      style: {
        color: "#3b82f6",
        weight: 4,
        opacity: 0.8,
      },
    }).addTo(mapRef.current);

    // Update markers with numbers
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];

    optimizeResult.optimizedOrder.forEach((waypoint, index) => {
      const customIcon = L.divIcon({
        className: "custom-marker",
        html: `<div style="
          width: 30px;
          height: 30px;
          border-radius: 50%;
          background-color: #3b82f6;
          border: 3px solid white;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 14px;
        ">${index + 1}</div>`,
        iconSize: [30, 30],
        iconAnchor: [15, 15],
      });

      const marker = markers.find(m => m.id === waypoint.waypointId);
      if (marker) {
        const leafletMarker = L.marker([waypoint.lat, waypoint.lng], { icon: customIcon })
          .addTo(mapRef.current!)
          .bindPopup(`
            <div style="min-width: 150px;">
              <strong>#${index + 1} - ${marker.orderNumber}</strong><br/>
              ${marker.customerName}<br/>
              <span style="color: #666;">${marker.cemeteryName}, ${marker.cemeteryCity}</span>
            </div>
          `);
        markersRef.current.push(leafletMarker);
      }
    });
  }, [optimizeResult, markers]);

  // fitBounds function to zoom to all markers
  const fitBounds = useCallback(() => {
    if (mapRef.current && markers.length > 0) {
      const bounds = L.latLngBounds(markers.map(m => [m.lat, m.lng]));
      mapRef.current.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [markers]);

  return { fitBounds };
}
